import { buildDraftSnapshot, buildTaskSnapshotEntry } from "./build-draft-snapshot"
import {
    normalizeScheduleMetadataToMacroIds,
    normalizeScheduleToMacroIds,
    normalizeTaskToMacroIds,
} from "authoring/routes/macro/lib/macro-conversion"
import { collectTaskScopeIdentifiers } from "./authoring-recommendation-edit-scopes"
import { isScheduleSavePerfEnabled, logScheduleSavePerf } from "./schedule-save-perf"

/** Forces full task walk when structure or unknown edits occur. */
export const SAVE_PREP_FORCE_FULL_SCOPE = "save:full"

/** Edit scopes that only touch schedule-level fields — skip task indexing and task macro normalization. */
export const METADATA_ONLY_SAVE_PREP_SCOPES = new Set([
    "schedule:title",
    "schedule:code",
    "intro:content",
    "intro:note",
])

/**
 * @param {string[]} scopes
 * @returns {boolean}
 */
export function requiresFullCacheRebuild(scopes = []) {
    if (!scopes.length) return true
    return scopes.includes(SAVE_PREP_FORCE_FULL_SCOPE)
}

/**
 * @param {string[]} scopes
 * @returns {boolean}
 */
export function requiresFullSavePrep(scopes = []) {
    if (!scopes.length) return true
    if (scopes.includes(SAVE_PREP_FORCE_FULL_SCOPE)) return true
    return scopes.some((scope) => !METADATA_ONLY_SAVE_PREP_SCOPES.has(scope))
}

/**
 * @param {string[]} scopes
 * @returns {boolean}
 */
export function isMetadataOnlySavePrep(scopes = []) {
    if (!scopes.length) return false
    if (scopes.includes(SAVE_PREP_FORCE_FULL_SCOPE)) return false
    return scopes.every((scope) => METADATA_ONLY_SAVE_PREP_SCOPES.has(scope))
}

/**
 * @param {string} scope
 * @returns {boolean}
 */
export function isTaskSavePrepScope(scope) {
    return scope.startsWith("task:") || scope.startsWith("taskNumber:")
}

/**
 * @param {object} draft
 * @returns {boolean}
 */
export function isMetadataOnlySavePayload(draft) {
    return Boolean(draft) && !Array.isArray(draft.tasks)
}

/**
 * @returns {{
 *   reset: () => void,
 *   prepare: (params: { draft: object, macroMap: object, macroMapKey?: string, scopes?: string[] }) => object | null,
 *   stats: { hits: number, misses: number, fullRebuilds: number, metadataUpdates: number, reindexes: number, partialRebuilds: number }
 * }}
 */
export function createDraftSavePayloadCache() {
    const stats = {
        hits: 0,
        misses: 0,
        fullRebuilds: 0,
        metadataUpdates: 0,
        reindexes: 0,
        partialRebuilds: 0,
    }

    let macroMapKey = ""
    let taskOrderKey = ""
    let taskIdentityKey = ""
    const taskEntries = new Map()

    return {
        reset,
        prepare,
        stats,
    }

    function reset() {
        macroMapKey = ""
        taskOrderKey = ""
        taskIdentityKey = ""
        taskEntries.clear()
    }

    function prepare({ draft, macroMap, macroMapKey: nextMacroMapKey = "", scopes = [] }) {
        if (!draft) return null

        const perfStart = isScheduleSavePerfEnabled() ? performance.now() : 0

        if (nextMacroMapKey !== macroMapKey) {
            reset()
            macroMapKey = nextMacroMapKey
        }

        const tasks = Array.isArray(draft.tasks) ? draft.tasks : []
        const nextOrderKey = buildTaskOrderKey(tasks)
        const nextIdentityKey = buildTaskIdentityKey(tasks)

        if (!taskEntries.size || requiresFullCacheRebuild(scopes)) {
            if (
                taskEntries.size &&
                scopes.includes(SAVE_PREP_FORCE_FULL_SCOPE) &&
                nextIdentityKey === taskIdentityKey &&
                nextOrderKey !== taskOrderKey
            ) {
                return finishSavePrep(
                    "reindex",
                    reindexCachedTasks({ draft, macroMap, tasks, nextOrderKey }),
                    scopes,
                    perfStart
                )
            }

            return finishSavePrep(
                "full-rebuild",
                fullRebuild({ draft, macroMap, tasks, nextOrderKey, nextIdentityKey }),
                scopes,
                perfStart
            )
        }

        if (isMetadataOnlySavePrep(scopes)) {
            return finishSavePrep("metadata-only", metadataUpdate({ draft, macroMap }), scopes, perfStart)
        }

        const scopedIdentifiers = collectScopedTaskIdentifiers(scopes, draft)
        if (scopedIdentifiers.size) {
            return finishSavePrep(
                "partial-cache",
                partialTaskRebuild({
                    draft,
                    macroMap,
                    tasks,
                    scopedIdentifiers,
                    nextOrderKey,
                    nextIdentityKey,
                }),
                scopes,
                perfStart
            )
        }

        return finishSavePrep(
            "full-rebuild",
            fullRebuild({ draft, macroMap, tasks, nextOrderKey, nextIdentityKey }),
            scopes,
            perfStart
        )
    }

    function finishSavePrep(path, result, scopes, perfStart) {
        if (isScheduleSavePerfEnabled()) {
            logScheduleSavePerf("save-prep", {
                path,
                scopes,
                hits: stats.hits,
                misses: stats.misses,
                fullRebuilds: stats.fullRebuilds,
                metadataUpdates: stats.metadataUpdates,
                reindexes: stats.reindexes,
                partialRebuilds: stats.partialRebuilds,
                durationMs: Math.round(performance.now() - perfStart),
            })
        }
        return result
    }

    function fullRebuild({ draft, macroMap, tasks, nextOrderKey, nextIdentityKey }) {
        stats.fullRebuilds += 1
        stats.misses += tasks.length

        const normalizedDraft = normalizeScheduleToMacroIds(draft, macroMap)
        const payload = buildDraftSnapshot({ draft: normalizedDraft, indexTasks: true })

        taskEntries.clear()
        for (const task of normalizedDraft.tasks ?? []) {
            const cacheKey = resolveTaskCacheKey(task)
            if (!cacheKey) continue
            taskEntries.set(cacheKey, buildTaskSnapshotEntry(task))
        }

        taskOrderKey = nextOrderKey
        taskIdentityKey = nextIdentityKey
        return payload
    }

    function metadataUpdate({ draft, macroMap }) {
        stats.metadataUpdates += 1

        const normalizedMetadata = normalizeScheduleMetadataToMacroIds(draft, macroMap)
        return buildDraftSnapshot({
            draft: normalizedMetadata,
            indexTasks: true,
            includeTasks: false,
        })
    }

    function partialTaskRebuild({ draft, macroMap, tasks, scopedIdentifiers, nextOrderKey, nextIdentityKey }) {
        stats.partialRebuilds += 1

        const matchedTasks = findTasksMatchingIdentifiers(tasks, scopedIdentifiers)
        stats.misses += matchedTasks.length
        stats.hits += Math.max(0, tasks.length - matchedTasks.length)

        for (const task of matchedTasks) {
            const cacheKey = resolveTaskCacheKey(task)
            if (!cacheKey) continue
            const normalizedTask = normalizeTaskToMacroIds(task, macroMap)
            taskEntries.set(cacheKey, buildTaskSnapshotEntry(normalizedTask))
        }

        taskOrderKey = nextOrderKey
        taskIdentityKey = nextIdentityKey

        const normalizedMetadata = normalizeScheduleMetadataToMacroIds(draft, macroMap)
        const metadataSnapshot = buildDraftSnapshot({
            draft: normalizedMetadata,
            indexTasks: true,
            includeTasks: false,
        })

        return {
            ...metadataSnapshot,
            tasks: assembleTaskSnapshots({ tasks, macroMap }),
        }
    }

    function reindexCachedTasks({ draft, macroMap, tasks, nextOrderKey }) {
        stats.reindexes += 1
        stats.hits += tasks.length

        taskOrderKey = nextOrderKey

        const normalizedMetadata = normalizeScheduleMetadataToMacroIds(draft, macroMap)
        const metadataSnapshot = buildDraftSnapshot({
            draft: normalizedMetadata,
            indexTasks: true,
            includeTasks: false,
        })

        return {
            ...metadataSnapshot,
            tasks: assembleTaskSnapshots({ tasks, macroMap }),
        }
    }

    function assembleTaskSnapshots({ tasks, macroMap }) {
        return tasks.map((task, index) => {
            const cacheKey = resolveTaskCacheKey(task)
            const cached = cacheKey ? taskEntries.get(cacheKey) : null
            if (!cached) {
                const normalizedTask = normalizeTaskToMacroIds(task, macroMap)
                const entry = buildTaskSnapshotEntry(normalizedTask, { index: index + 1 })
                if (cacheKey) taskEntries.set(cacheKey, buildTaskSnapshotEntry(normalizedTask))
                return entry
            }

            return { ...cached, index: index + 1 }
        })
    }
}

/**
 * @param {object} params
 * @param {object} params.draft
 * @param {object} params.macroMap
 * @param {string} [params.macroMapKey]
 * @param {string[]} [params.scopes]
 * @param {ReturnType<typeof createDraftSavePayloadCache>} [params.cache]
 * @returns {object | null}
 */
export function buildSavePrepPayload({ draft, macroMap, macroMapKey = "", scopes = [], cache }) {
    if (!draft) return null

    if (cache) {
        return cache.prepare({ draft, macroMap, macroMapKey, scopes })
    }

    const perfStart = isScheduleSavePerfEnabled() ? performance.now() : 0

    if (requiresFullSavePrep(scopes)) {
        const normalizedDraft = normalizeScheduleToMacroIds(draft, macroMap)
        const result = buildDraftSnapshot({ draft: normalizedDraft, indexTasks: true })
        if (isScheduleSavePerfEnabled()) {
            logScheduleSavePerf("save-prep", {
                path: "full-rebuild-no-cache",
                scopes,
                durationMs: Math.round(performance.now() - perfStart),
            })
        }
        return result
    }

    const normalizedDraft = normalizeScheduleMetadataToMacroIds(draft, macroMap)
    const result = buildDraftSnapshot({ draft: normalizedDraft, includeTasks: false })
    if (isScheduleSavePerfEnabled()) {
        logScheduleSavePerf("save-prep", {
            path: "metadata-only-no-cache",
            scopes,
            durationMs: Math.round(performance.now() - perfStart),
        })
    }
    return result
}

export function resolveTaskCacheKey(task) {
    if (!task) return ""

    for (const value of [task.id, task.taskId, task.temporaryTaskId, task.headerTaskId]) {
        const identifier = `${value || ""}`.trim()
        if (identifier) return identifier
    }

    return ""
}

export function buildTaskOrderKey(tasks = []) {
    return tasks.map((task) => resolveTaskCacheKey(task)).join("|")
}

export function buildTaskIdentityKey(tasks = []) {
    return tasks
        .map((task) => resolveTaskCacheKey(task))
        .filter(Boolean)
        .sort()
        .join("|")
}

export function collectScopedTaskIdentifiers(scopes = [], draft) {
    const identifiers = new Set()

    for (const scope of scopes) {
        if (!isTaskSavePrepScope(scope)) continue

        if (scope.startsWith("task:")) {
            const [, identifier] = scope.split(":")
            if (identifier && identifier !== "*") identifiers.add(identifier)
            continue
        }

        if (scope.startsWith("taskNumber:")) {
            const [, taskNumberValue] = scope.split(":")
            const taskNumber = Number(taskNumberValue)
            const task = findTaskBySequenceNumber(draft?.tasks, taskNumber)
            for (const identifier of collectTaskScopeIdentifiers(task)) {
                identifiers.add(identifier)
            }
        }
    }

    return identifiers
}

export function findTasksMatchingIdentifiers(tasks = [], identifiers = new Set()) {
    if (!identifiers.size) return []

    return tasks.filter((task) => collectTaskScopeIdentifiers(task).some((identifier) => identifiers.has(identifier)))
}

function findTaskBySequenceNumber(tasks, taskNumber) {
    if (!Number.isInteger(taskNumber) || taskNumber <= 0) return null

    let counter = 0
    for (const task of Array.isArray(tasks) ? tasks : []) {
        if (task?.isHeader) continue
        counter += 1
        if (counter === taskNumber) return task
    }

    return null
}
